'use client';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import '@/components/marketing/marketing.css';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  Factory,
  FileText,
  FolderKanban,
  Heart,
  Layers3,
  Package,
  PanelTop,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  WalletCards,
  Wrench,
  Zap,
} from 'lucide-react';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import type { PublicSearchEntry } from '@/lib/marketing/search';

/* ============================================================
   MENTROX ONEHR — IMAGE-MATCH HOMEPAGE
   Design reference:
   - Dark navy hero
   - White/light SaaS sections
   - Orange primary accent
   - Large product dashboard mockups
   - 5-column module grid
   - Workforce / industries / testimonials / CTA / footer
   - No pricing section
============================================================ */

const platformModules = [
  {
    title: 'Employee Management',
    description: 'Manage your entire employee lifecycle.',
    icon: Users,
    tone: 'orange',
    href: '/platform/employee-management',
  },
  {
    title: 'Workforce Management',
    description: 'Connect assignments, shifts and recorded time.',
    icon: BriefcaseBusiness,
    tone: 'blue',
    href: '/platform/workforce-management',
  },
  {
    title: 'Attendance Management',
    description: 'Flexible and accurate attendance tracking.',
    icon: ClipboardCheck,
    tone: 'green',
    href: '/platform/attendance',
  },
  {
    title: 'Leave Management',
    description: 'Simplify leave and holiday management.',
    icon: CalendarDays,
    tone: 'amber',
    href: '/platform/leave',
  },
  {
    title: 'Payroll Management',
    description: 'Review payroll and configured deductions.',
    icon: WalletCards,
    tone: 'red',
    href: '/platform/payroll',
  },
  {
    title: 'Performance Management',
    description: 'Set goals, track progress and drive growth.',
    icon: Target,
    tone: 'green',
    href: '/platform/performance',
  },
  {
    title: 'Project Planning',
    description: 'Plan, assign and track projects.',
    icon: FolderKanban,
    tone: 'blue',
    href: '/solutions/project-planning',
  },
  {
    title: 'Production Management',
    description: 'Supporting workflows; production planning is planned.',
    icon: Factory,
    tone: 'purple',
    href: '/solutions/production-management',
  },
  {
    title: 'Breakdown Management',
    description: 'Assigned-asset issues and task follow-up.',
    icon: Wrench,
    tone: 'red',
    href: '/solutions/breakdown-management',
  },
  {
    title: 'Inventory Management',
    description: 'Track assets, inventory and stock.',
    icon: Package,
    tone: 'blue',
    href: '/solutions/inventory-management',
  },
  {
    title: 'Recruitment & Onboarding',
    description: 'Hire and onboard top talent.',
    icon: Users,
    tone: 'purple',
    href: '/platform/recruitment',
  },
  {
    title: 'Document Management',
    description: 'Secure and organized employee documents.',
    icon: FileText,
    tone: 'orange',
    href: '/platform/documents',
  },
  {
    title: 'Analytics & Reports',
    description: 'Get real-time insights and reports.',
    icon: BarChart3,
    tone: 'green',
    href: '/platform/analytics',
  },
  {
    title: 'AI Assistant',
    description: 'Your intelligent HR assistant.',
    icon: Sparkles,
    tone: 'blue',
    href: '/platform/ai',
  },
  {
    title: 'Organization Management',
    description: 'Manage departments, designations and more.',
    icon: Building2,
    tone: 'amber',
    href: '/platform/organization',
  },
];

const industries = [
  { label: 'Manufacturing', href: '/industries/manufacturing', icon: Factory },
  { label: 'Garments & Textiles', href: '/industries/garments', icon: Layers3 },
  { label: 'IT & Technology', href: '/industries/technology', icon: PanelTop },
  { label: 'Construction', href: '/industries/construction', icon: Wrench },
  { label: 'Healthcare', href: '/industries/healthcare', icon: Heart },
  { label: 'Retail & Distribution', href: '/industries/retail', icon: Package },
  { label: 'Professional Services', href: '/industries/services', icon: BriefcaseBusiness },
  { label: 'More Industries', href: '/industries', icon: Building2 },
];

const lifecycle = [
  { label: 'Recruit', subtitle: 'Hire the right people', icon: Users },
  { label: 'Onboard', subtitle: 'Give a great start', icon: ClipboardCheck },
  { label: 'Work', subtitle: 'Manage day to day', icon: BriefcaseBusiness },
  { label: 'Pay', subtitle: 'Process payroll', icon: WalletCards },
  { label: 'Perform', subtitle: 'Drive growth', icon: Activity },
  { label: 'Operate', subtitle: 'Run your operations', icon: Factory },
  { label: 'Grow', subtitle: 'Build tomorrow', icon: BarChart3 },
];

function PrimaryButton({
  href = '#',
  children,
  outline = false,
}: {
  href?: string;
  children: React.ReactNode;
  outline?: boolean;
}) {
  return (
    <Link
      href={href}
      className={[
        'group inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold transition-all duration-300',
        outline
          ? 'border border-white/20 bg-white/5 text-white hover:border-white/40 hover:bg-white/10'
          : 'bg-[#c94e00] text-white shadow-[0_18px_50px_rgba(255,106,0,.24)] hover:-translate-y-0.5 hover:bg-orange-800',
      ].join(' ')}
    >
      {children}
      <ArrowUpRight
        size={16}
        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </Link>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  dark = false,
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  dark?: boolean;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div
        className={[
          'text-[11px] font-bold uppercase tracking-[0.22em]',
          dark ? 'text-[#FF8A3D]' : 'text-[#FF6A00]',
        ].join(' ')}
      >
        {eyebrow}
      </div>

      <h2
        className={[
          'mt-4 text-3xl font-bold tracking-[-0.04em] sm:text-4xl lg:text-5xl',
          dark ? 'text-white' : 'text-[#0A1C33]',
        ].join(' ')}
      >
        {title}
      </h2>

      {description ? (
        <p
          className={[
            'mx-auto mt-4 max-w-2xl text-sm leading-7 sm:text-base',
            dark ? 'text-white/55' : 'text-[#60728A]',
          ].join(' ')}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

function toneClass(tone: string) {
  switch (tone) {
    case 'blue':
      return 'bg-blue-50 text-blue-600';
    case 'green':
      return 'bg-emerald-50 text-emerald-600';
    case 'amber':
      return 'bg-amber-50 text-amber-600';
    case 'red':
      return 'bg-red-50 text-red-500';
    case 'purple':
      return 'bg-violet-50 text-violet-600';
    default:
      return 'bg-orange-50 text-orange-600';
  }
}

function HeroDashboard() {
  const [active, setActive] = useState('Dashboard');

  const sideItems = [
    'Dashboard',
    'Employees',
    'Attendance',
    'Leave',
    'Payroll',
    'Performance',
    'Projects',
    'Production',
    'Inventory',
    'Reports',
    'Settings',
  ];

  return (
    <div className="relative mx-auto w-full max-w-[760px] sm:px-0">
      <div className="absolute -inset-10 rounded-full bg-orange-500/10 blur-[110px]" />

      <div className="relative rounded-[22px] border border-white/15 bg-white/95 p-1.5 shadow-[0_35px_80px_rgba(0,0,0,.38)] sm:rounded-[28px] sm:p-2 sm:shadow-[0_50px_120px_rgba(0,0,0,.45)]">
        <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-[#F5F7FA] sm:rounded-[23px]">
          <div className="flex h-11 items-center justify-between border-b border-slate-200 bg-white px-3 sm:h-12 sm:px-4">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#c94e00] text-xs font-black text-white">
                M
              </div>
              <div className="text-[11px] font-bold text-[#0A1C33]">Mentrox OneHR</div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden h-6 w-28 items-center rounded-full bg-slate-50 px-3 text-[8px] text-slate-600 sm:flex">
                Search...
              </div>
              <Bell size={13} className="text-slate-500" />
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-[8px] font-bold text-orange-600">
                AD
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr]">
            <aside className="hidden border-r border-slate-200 bg-white p-3 sm:block">
              <div className="mb-4 px-2 text-[8px] font-bold uppercase tracking-[0.14em] text-slate-600">
                OneHR
              </div>

              <div className="space-y-1">
                {sideItems.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setActive(item)}
                    className={[
                      'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[9px] transition-colors',
                      active === item
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-500 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <span className="h-1.5 w-1.5 rounded-full border border-current" />
                    {item}
                  </button>
                ))}
              </div>
            </aside>

            <div className="min-w-0 p-2.5 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[8px] uppercase tracking-[0.17em] text-slate-600">
                    Workforce overview
                  </div>
                  <div className="mt-1 text-[13px] font-bold text-[#0A1C33] sm:text-base">
                    Good Morning, Admin
                  </div>
                  <div className="mt-1 text-[8px] text-slate-600">
                    Here&apos;s what&apos;s happening today.
                  </div>
                </div>

                <div className="rounded-lg bg-slate-100 px-2 py-1 text-[8px] text-slate-500">
                  This Month
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  ['2,482', 'Employees', '+12%'],
                  ['96.8%', 'Attendance', '+2.4%'],
                  ['₹42.6L', 'Payroll', '+8.2%'],
                  ['126', 'Projects', '+15%'],
                ].map(([value, label, trend]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-slate-200 bg-white p-2 sm:p-3"
                  >
                    <div className="flex items-center justify-between">
                      <Users size={12} className="text-orange-500" />
                      <span className="text-[7px] font-bold text-emerald-500">
                        ↑ {trend.replace('+', '')}
                      </span>
                    </div>
                    <div className="mt-3 text-[13px] font-bold text-[#0A1C33] sm:text-sm">
                      {value}
                    </div>
                    <div className="mt-1 text-[7px] text-slate-600">{label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 grid gap-2.5 sm:grid-cols-[1.35fr_0.9fr] sm:gap-3 lg:grid-cols-[1.5fr_0.8fr]">
                <div className="rounded-xl border border-slate-200 bg-white p-2.5 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[8px] text-slate-600">Workforce Trend</div>
                      <div className="mt-1 text-[10px] font-bold text-[#0A1C33]">
                        Attendance intelligence
                      </div>
                    </div>
                    <BarChart3 size={14} className="text-orange-500" />
                  </div>

                  <div className="mt-5 flex h-28 items-end gap-1.5">
                    {[44, 62, 54, 74, 67, 84, 76, 92, 88, 96, 91, 98].map((height, index) => (
                      <div key={index} className="flex h-full flex-1 items-end">
                        <div
                          className={[
                            'w-full rounded-t-md',
                            index === 9 ? 'bg-orange-500' : 'bg-blue-100',
                          ].join(' ')}
                          style={{ height: `${height}%` }}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 flex justify-between text-[7px] text-slate-300">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S', 'M', 'T', 'W', 'T', 'F'].map((d, i) => (
                      <span key={i}>{d}</span>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-4">
                  <div className="text-[8px] text-slate-600">Quick Actions</div>

                  <div className="mt-2 space-y-1.5 sm:mt-3 sm:space-y-2">
                    {[
                      ['Add Employee', Users],
                      ['Mark Attendance', Clock3],
                      ['Generate Payslip', WalletCards],
                      ['Create Project', FolderKanban],
                      ['View Reports', BarChart3],
                    ].map(([label, Icon]) => (
                      <div
                        key={String(label)}
                        className="flex items-center gap-2 rounded-lg border border-slate-100 p-2"
                      >
                        {React.createElement(Icon as React.ElementType, {
                          size: 11,
                          className: 'text-blue-500',
                        })}
                        <span className="text-[8px] text-slate-500">{String(label)}</span>
                        <ArrowRight size={9} className="ml-auto text-slate-300" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-1.5 sm:mt-3 sm:gap-2">
                <div className="rounded-xl border border-slate-200 bg-white p-2.5 sm:p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={13} className="text-emerald-500" />
                    <span className="text-[8px] font-bold text-slate-700">
                      Payroll inputs reviewed
                    </span>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-2.5 sm:p-3">
                  <div className="flex items-center gap-2">
                    <Bell size={13} className="text-orange-500" />
                    <span className="text-[8px] font-bold text-slate-700">
                      07 approvals requiring attention
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -right-6 top-20 hidden rounded-2xl border border-white/20 bg-[#102E52]/95 p-4 shadow-2xl sm:block">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={15} className="text-emerald-400" />
          <div>
            <div className="text-[8px] text-white/65">Payroll</div>
            <div className="text-[10px] font-semibold text-white">Review before processing</div>
          </div>
        </div>
      </div>

      <div className="absolute -left-5 bottom-8 hidden rounded-2xl border border-white/20 bg-[#102E52]/95 p-4 shadow-2xl sm:block">
        <div className="text-[8px] text-white/65">New employee</div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-[8px] font-bold text-white">
            AS
          </div>
          <div>
            <div className="text-[10px] font-semibold text-white">Arjun Sharma</div>
            <div className="text-[8px] text-white/65">Engineering</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#06162C] pt-16 text-white sm:pt-32 lg:pt-28">
      <div className="absolute inset-0 opacity-[0.11] [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:72px_72px]" />

      <div className="absolute -left-52 -top-40 h-[700px] w-[700px] rounded-full bg-blue-500/10 blur-[160px]" />
      <div className="absolute -right-40 top-20 h-[650px] w-[650px] rounded-full bg-orange-500/10 blur-[150px]" />

      <div className="relative mx-auto grid min-h-0 max-w-[1500px] items-center gap-10 px-4 pb-12 sm:gap-12 sm:px-5 sm:pb-16 lg:min-h-[700px] lg:grid-cols-[0.95fr_1.25fr] lg:px-8 lg:pb-20">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/15 bg-orange-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
            Intelligent Workforce Platform
          </div>

          <h1 className="mt-6 max-w-[11ch] text-[42px] font-bold leading-[1.02] tracking-[-0.055em] text-white sm:max-w-none sm:text-6xl lg:text-[68px] lg:leading-[1.02]">
            Run your entire <span className="text-[#FF6A00]">workforce</span> from one intelligent
            platform.
          </h1>

          <p className="mt-5 max-w-lg text-[13px] leading-6 text-white/60 sm:mt-6 sm:text-base sm:leading-7">
            From employee management and attendance to payroll, performance, projects and supported
            operations — Mentrox OneHR connects everything in one place.
          </p>

          <div className="mt-7 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap">
            <PrimaryButton href="/request-demo">Request a Demo</PrimaryButton>

            <PrimaryButton href="#platform" outline>
              Explore the Platform
            </PrimaryButton>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-2 sm:mt-8 sm:grid-cols-4 sm:gap-3">
            {[
              ['All-in-One', 'Platform', Layers3],
              ['Secure &', 'Compliant', ShieldCheck],
              ['Built for', 'Modern Teams', Users],
              ['Scalable', 'for Growth', Zap],
            ].map(([a, b, Icon]) => (
              <div
                key={String(a)}
                className="rounded-2xl border border-white/10 bg-white/[0.035] p-2.5 sm:p-3"
              >
                {React.createElement(Icon as React.ElementType, {
                  size: 15,
                  className: 'text-orange-300',
                })}
                <div className="mt-2 text-[10px] font-semibold text-white">{String(a)}</div>
                <div className="text-[9px] text-white/65 sm:text-[10px]">{String(b)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative w-full min-w-0 lg:translate-y-3">
          <div>
            <p className="mb-3 text-xs text-white/70">
              Illustrative dashboard · sample data, not customer results
            </p>
            <HeroDashboard />
          </div>

          <div className="mt-6 text-center text-[12px] font-semibold italic text-orange-300 lg:text-right">
            Empowering People for a Better Tomorrow
          </div>
        </div>
      </div>
    </section>
  );
}

function LifecycleSection() {
  return (
    <section className="border-b border-slate-100 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-5 lg:px-8">
        <SectionHeading
          eyebrow="The Complete Ecosystem"
          title="Everything your workforce needs."
          description="People, operations, finance and intelligence — all connected in Mentrox OneHR."
        />

        <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:mt-12 sm:flex sm:flex-col sm:gap-5 lg:flex-row lg:items-start lg:justify-between">
          {lifecycle.map((item, index) => {
            const Icon = item.icon;

            return (
              <React.Fragment key={item.label}>
                <div className="flex flex-1 flex-col items-center text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-orange-500 shadow-sm ring-1 ring-slate-100 sm:h-14 sm:w-14">
                    <Icon size={20} />
                  </div>

                  <div className="mt-3 text-[13px] font-bold text-[#0A1C33] sm:text-sm">
                    {item.label}
                  </div>

                  <div className="mt-1 max-w-[135px] text-[10px] leading-5 text-slate-600 sm:text-[11px]">
                    {item.subtitle}
                  </div>
                </div>

                {index < lifecycle.length - 1 ? (
                  <div className="hidden flex-1 items-center lg:flex">
                    <div className="h-px w-full border-t border-dashed border-orange-200" />
                    <ArrowRight size={14} className="-ml-1 text-orange-300" />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PlatformSection() {
  return (
    <section id="platform" className="bg-[#F7F8FA] py-16 sm:py-24">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-5 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-5 sm:gap-6 md:flex-row md:items-end">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-orange-500">
              Our Modules
            </div>
            <h2 className="mt-3 max-w-3xl text-[30px] font-bold leading-tight tracking-[-0.04em] text-[#0A1C33] sm:text-4xl">
              A complete platform for modern businesses.
            </h2>
            <p className="mt-3 text-sm text-slate-500">
              From HR to operations, everything you need to manage your workforce efficiently.
            </p>
          </div>

          <Link
            href="/platform"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-xs font-bold text-[#0A1C33] shadow-sm hover:border-orange-200 hover:text-orange-600"
          >
            Explore All Modules
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {platformModules.map((module, index) => {
            const Icon = module.icon;

            return (
              <Link
                key={module.title}
                href={module.href}
                className={[
                  'group rounded-2xl border border-slate-200 bg-white p-3 transition-all duration-300 sm:p-5 hover:-translate-y-1 hover:border-orange-200 hover:shadow-[0_18px_45px_rgba(9,30,66,.08)]',
                  index >= 4 ? 'hidden sm:block' : '',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex h-10 w-10 items-center justify-center rounded-xl',
                    toneClass(module.tone),
                  ].join(' ')}
                >
                  <Icon size={18} />
                </div>

                <h3 className="mt-3 text-[11px] font-bold leading-4 text-[#0A1C33] sm:mt-4 sm:text-sm">
                  {module.title}
                </h3>

                <p className="mt-1.5 min-h-[40px] text-[9px] leading-4 text-slate-600 sm:mt-2 sm:text-[11px] sm:leading-5">
                  {module.description}
                </p>

                <div className="mt-3 flex items-center gap-1 text-[9px] font-bold text-orange-500 sm:mt-4 sm:text-[11px]">
                  Explore
                  <ArrowRight
                    size={12}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkforceSection() {
  return (
    <section id="solutions" className="bg-white py-16 sm:py-24">
      <div className="mx-auto grid max-w-[1320px] items-center gap-10 px-4 sm:px-5 lg:grid-cols-[0.78fr_1.22fr] lg:px-8">
        <div className="max-w-lg">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-orange-500">
            Workforce Management
          </div>

          <h2 className="mt-4 text-4xl font-bold tracking-[-0.05em] text-[#0A1C33] sm:text-5xl">
            Know where your workforce stands.
          </h2>

          <p className="mt-4 text-sm leading-7 text-slate-500">
            Review recorded employee, attendance and leave information before making workforce
            decisions.
          </p>

          <div className="mt-6 space-y-3">
            {[
              'Recorded workforce information',
              'Configured shifts and assignments',
              'Location-aware attendance',
              'Department-wise insights',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle2 size={17} className="text-orange-500" />
                <span className="text-sm font-medium text-slate-600">{item}</span>
              </div>
            ))}
          </div>

          <Link
            href="/platform/workforce-management"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-orange-200 px-5 py-3 sm:mt-7 sm:w-auto text-xs font-bold text-orange-600 hover:bg-orange-50"
          >
            Explore Workforce Management
            <ArrowRight size={14} />
          </Link>
        </div>

        <div
          aria-label="Illustrative workforce dashboard with sample data"
          className="rounded-[24px] border border-slate-200 bg-[#F6F8FB] p-2.5 shadow-[0_18px_55px_rgba(9,30,66,.07)] sm:rounded-[28px] sm:p-6"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-slate-500">
                  Illustrative workforce overview · sample data
                </div>
                <div className="mt-1 text-lg font-bold text-[#0A1C33]">This Month</div>
              </div>

              <div className="rounded-lg border border-slate-200 px-3 py-2 text-[10px] text-slate-500">
                This Month <ChevronDown size={11} className="ml-1 inline" />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                ['2,482', 'Total Employees', 'blue'],
                ['2,012', 'Present', 'green'],
                ['420', 'Remote', 'blue'],
                ['82', 'On Leave', 'orange'],
                ['37', 'Late', 'red'],
              ].map(([value, label, tone]) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div className="text-[8px] text-slate-600">{label}</div>
                  <div
                    className={[
                      'mt-2 text-base font-bold',
                      tone === 'red'
                        ? 'text-red-500'
                        : tone === 'green'
                          ? 'text-emerald-500'
                          : tone === 'orange'
                            ? 'text-orange-500'
                            : 'text-[#0A1C33]',
                    ].join(' ')}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_0.8fr]">
              <div className="rounded-xl border border-slate-100 p-4">
                <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600">
                  Department Attendance
                </div>

                <div className="mt-4 space-y-3">
                  {[
                    ['Production', 96],
                    ['Engineering', 92],
                    ['Sales', 88],
                    ['HR', 86],
                    ['Administration', 90],
                  ].map(([label, value]) => (
                    <div key={label as string}>
                      <div className="mb-1 flex justify-between text-[8px] text-slate-500">
                        <span>{label as string}</span>
                        <span>{value}%</span>
                      </div>

                      <div className="h-2 rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
                          style={{ width: `${value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-slate-100 p-4">
                <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600">
                  Work Location
                </div>

                <div className="mt-5 flex items-center justify-center">
                  <div className="relative flex h-36 w-36 items-center justify-center rounded-full border-[22px] border-blue-100">
                    <div className="absolute inset-[-22px] rounded-full border-[22px] border-transparent border-t-orange-400 border-r-orange-300" />
                    <div className="text-center">
                      <div className="text-xl font-bold text-[#0A1C33]">62%</div>
                      <div className="text-[8px] text-slate-600">Office</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 text-[8px] text-slate-600">
                  <span>Office 62%</span>
                  <span>Remote 17%</span>
                  <span>Site 14%</span>
                  <span>Other 7%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OperationsSection() {
  const operations = [
    {
      title: 'Project Planning',
      icon: FolderKanban,
      metric: '126',
      label: 'active projects',
      href: '/solutions/project-planning',
    },
    {
      title: 'Production Management',
      icon: Factory,
      metric: 'Planned',
      label: 'dedicated production planning',
      href: '/solutions/production-management',
    },
    {
      title: 'Breakdown Management',
      icon: Wrench,
      metric: 'Available',
      label: 'assigned-asset issue reporting',
      href: '/solutions/breakdown-management',
    },
    {
      title: 'Inventory Management',
      icon: Package,
      metric: 'Available',
      label: 'finance inventory records',
      href: '/solutions/inventory-management',
    },
  ];

  return (
    <section className="bg-[#F7F8FA] py-16 sm:py-24">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-5 lg:px-8">
        <SectionHeading
          eyebrow="Workforce Operations"
          title={
            <>
              Connect people to the <span className="text-orange-500">work.</span>
            </>
          }
          description="Explore project and inventory workflows, plus the current scope of production and equipment issue support."
        />

        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {operations.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                key={item.title}
                href={item.href}
                className="group rounded-[22px] border border-slate-200 bg-white p-5 transition-all hover:-translate-y-1 hover:border-orange-200 hover:shadow-[0_18px_45px_rgba(9,30,66,.08)]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                    <Icon size={18} />
                  </div>

                  <ArrowUpRight
                    size={16}
                    className="text-slate-300 transition-all group-hover:text-orange-500"
                  />
                </div>

                <h3 className="mt-6 text-lg font-bold text-[#0A1C33]">{item.title}</h3>

                <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-4">
                  <div>
                    <div className="text-2xl font-bold text-[#0A1C33]">{item.metric}</div>
                    <div className="mt-1 text-[10px] text-slate-600">{item.label}</div>
                  </div>

                  <div className="h-12 w-20 rounded-xl bg-gradient-to-tr from-blue-50 via-white to-orange-50" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-[1320px] px-4 sm:px-5 lg:px-8">
        <SectionHeading
          eyebrow="Built for Your Industry"
          title="Built for how your business works."
          description="Mentrox OneHR adapts to your industry needs."
        />

        <div className="mt-10 grid gap-3 grid-cols-2 sm:grid-cols-4 lg:grid-cols-8">
          {industries.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                href={item.href}
                key={item.label}
                className="group flex min-h-[100px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-3 text-center transition-all sm:min-h-[110px] sm:p-4 hover:-translate-y-1 hover:border-orange-200 hover:shadow-sm"
              >
                <Icon
                  size={21}
                  className="text-orange-500 transition-transform group-hover:scale-110"
                />
                <div className="mt-3 text-[11px] font-semibold leading-4 text-slate-600">
                  {item.label}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SecuritySection() {
  const items = [
    ['Tenant Isolation', ShieldCheck],
    ['Role-based Access', Users],
    ['Audit Trails', FileText],
    ['Session Controls', LockIcon],
  ] as const;

  return (
    <section id="security" className="bg-[#06162C] py-16 sm:py-24">
      <div className="mx-auto max-w-[1180px] px-4 sm:px-5 lg:px-8">
        <SectionHeading
          eyebrow="Enterprise Security"
          title="Company data. Deliberate access."
          description="Security controls designed for modern workforce operations and enterprise access."
          dark
        />

        <div className="mt-10 grid gap-3 sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(([label, Icon]) => (
            <div
              key={label}
              className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 text-center sm:p-6"
            >
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-orange-300">
                <Icon size={19} />
              </div>

              <div className="mt-4 text-sm font-bold text-white">{label}</div>
              <div className="mt-1 text-[11px] leading-5 text-white/65">
                Application access controls
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LockIcon({ size = 19, className = '' }: { size?: number; className?: string }) {
  return <ShieldCheck size={size} className={className} />;
}

function EvaluationResources() {
  return (
    <section className="bg-[#F7F8FA] py-12 sm:py-16">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Evaluate with confidence"
          title="Build your own product checklist."
          description="Use real workflows and clear scope to decide what fits your company."
        />
        <div className="mt-7 grid gap-4 md:grid-cols-3">
          {[
            [
              'Start with the setup guides',
              'Follow company structure, employee setup and permissions before introducing the first workflow.',
              '/resources/docs',
            ],
            [
              'Review the security approach',
              'Understand the implemented application controls and ask about deployment-specific requirements.',
              '/security',
            ],
            [
              'Bring a real scenario to the demo',
              'Discuss attendance exceptions, payroll review and the operational scope your business needs.',
              '/request-demo',
            ],
          ].map(([title, text, href]) => (
            <Link
              key={href}
              href={href}
              className="rounded-xl border border-slate-200 bg-white p-6"
            >
              <h2 className="text-lg font-bold">{title}</h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">{text}</p>
              <p className="mt-4 text-sm font-semibold text-orange-700">Explore →</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState(0);

  const questions = [
    'What is Mentrox OneHR?',
    'Which teams can use Mentrox?',
    'Does Mentrox support payroll and statutory deductions?',
    'Can we manage production workforce and operations?',
    'Can employees access their own HR information?',
    'Can Mentrox integrate with existing systems?',
  ];
  const answers = [
    'An HR and workforce application with employee records, attendance, leave, payroll, projects, performance and enabled business workflows.',
    'Companies can evaluate the supported workflows for their actual people and operating processes. Industry guides describe the relevant scope and limits.',
    'It supports configured EPF, ESI, professional tax and TDS workflows. Employers and qualified advisers must review current requirements; software support is not a compliance guarantee.',
    'Employee shifts, attendance, payroll, tasks and finance inventory support factory administration. Dedicated production execution and scheduling are planned, not implemented.',
    'Employee workspaces provide supported self-service subject to enabled modules and permissions.',
    'No specific external integration is promised here. Bring your requirements to the demo and confirm supported scope in writing.',
  ];

  return (
    <section id="resources" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-5 lg:px-8">
        <SectionHeading eyebrow="FAQ" title="Questions, answered." />

        <div className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white sm:mt-10">
          {questions.map((question, index) => (
            <div key={question}>
              <button
                type="button"
                aria-expanded={open === index}
                onClick={() => setOpen(open === index ? -1 : index)}
                className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left sm:px-5 sm:py-5"
              >
                <span className="text-sm font-semibold text-[#0A1C33]">{question}</span>

                <ChevronDown
                  size={17}
                  className={[
                    'shrink-0 text-slate-600 transition-transform',
                    open === index ? 'rotate-180' : '',
                  ].join(' ')}
                />
              </button>

              {open === index ? (
                <div className="px-5 pb-5 text-sm leading-7 text-slate-500">{answers[index]}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-[#06162C] py-14 text-white sm:py-20">
      <div className="absolute -right-20 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-orange-500/10 blur-[100px]" />

      <div className="relative mx-auto grid max-w-[1320px] items-center gap-8 px-4 sm:px-5 lg:grid-cols-[1fr_auto] lg:px-8">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-orange-300">
            Ready to transform your workforce?
          </div>

          <h2 className="mt-3 max-w-2xl text-4xl font-bold tracking-[-0.05em] sm:text-5xl">
            Build a better workforce operation.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/55">
            Bring people, operations, payroll and workforce intelligence together with Mentrox
            OneHR.
          </p>

          <div className="mt-7 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap">
            <PrimaryButton href="/request-demo">Request a Demo</PrimaryButton>

            <PrimaryButton href="/platform" outline>
              Explore the Platform
            </PrimaryButton>
          </div>
        </div>

        <div className="relative hidden h-56 w-80 lg:block">
          <div className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-orange-500 shadow-[0_20px_60px_rgba(255,106,0,.35)]">
            <div className="text-2xl font-black">M</div>
          </div>

          {[
            ['PM', 'left-2 top-4'],
            ['AK', 'right-2 top-12'],
            ['SR', 'left-14 bottom-3'],
            ['AS', 'right-16 bottom-0'],
          ].map(([initials, pos]) => (
            <div
              key={initials}
              className={`absolute ${pos} flex h-12 w-12 items-center justify-center rounded-full border-4 border-[#0B2342] bg-white text-[10px] font-bold text-[#0A1C33] shadow-xl`}
            >
              {initials}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePageClient({
  searchIndex,
}: {
  searchIndex: readonly PublicSearchEntry[];
}) {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#F7F8FA]">
      <MarketingHeader variant="light" searchIndex={searchIndex} />
      <Hero />
      <LifecycleSection />
      <PlatformSection />
      <WorkforceSection />
      <OperationsSection />
      <IndustriesSection />
      <SecuritySection />
      <EvaluationResources />
      <FAQSection />
      <FinalCTA />
      <MarketingFooter />
    </main>
  );
}
